// The game controller: owns the feed (local sim or the network room), turns input into seat inputs, routes events to
// FX / sound / HUD / profile, drives the camera and the CrazyGames gameplay clock, and runs the between-round meta
// (results payout → capsules → Burrow). Rendering is World's; screens are the HUD's; rules are the sim's.

import * as THREE from "three";
import { World, cosIndex, cosIds } from "./render/world";
import { LocalFeed, type Feed } from "./feed";
import { NetFeed } from "./net/feed";
import { WS_BASE } from "./platform/endpoint";
import * as input from "./input";
import * as audio from "./audio";
import * as cg from "./platform/cg";
import * as fp from "./platform/fp";
import * as store from "./meta/store";
import { applyRound, collectIncome, ensureDaily, firstTime, type RoundResult } from "./meta/profile";
import { install, type KitManifest } from "./ui/kit";
import { Hud } from "./ui/hud";
import type { PublicView, PrivateView, SimEvent, Cosmetics } from "../../shared/sim";
import * as T from "../../shared/tuning";
import { STALLS, DRAINS } from "../../shared/map";
import { genName, isGenName } from "../../shared/names";
import { json } from "./render/assets";
import * as tel from "./platform/telemetry";
import { BotSneak, BotInspector, HUMAN_SNEAK, type BotBrain } from "../../shared/bots";
/** QA only: ?qa=bot plays MY seat with the person-like bot from the human-model gate, through the real client path */
/** the guide arrow stays on until this many lifetime stashes */
const GUIDE_UNTIL_STASHES = 3;
const QA_BOT = new URLSearchParams(location.search).get("qa") === "bot";

export class Game {
  feed: Feed | null = null;
  hud: Hud;
  ready: Promise<void>;
  private last = performance.now();
  private pub: PublicView | null = null;
  private priv: PrivateView | null = null;
  private playing = false;               // CrazyGames gameplay clock state
  private roundsSinceAd = 0; private sessionStart = Date.now();
  private phase = "";
  private mark = 0;
  private publishedCode = "";
  /** the menu's backdrop: a live all-bot town behind the door, camera on a slow orbit (Wobble: the door is a beauty shot) */
  private attract: LocalFeed | null = null; private orbit = 0;
  private introRound = -1;
  private youSprite: THREE.Sprite | null = null;
  private aimSprite: THREE.Sprite | null = null;
  private youUntil = 0;

  constructor(public world: World, kitMan: KitManifest) {
    install(kitMan);
    this.hud = new Hud(this);
    this.ready = (async () => {
      const man = await json("./audio/manifest.json").catch(() => ({}));
      audio.init(man);
    })();
    input.bindStage(world.stage.renderer.domElement);
    // in-world markers: the painted arrow over my head (first seconds of a life) and over the Inspector's target
    const tex = new THREE.TextureLoader().load("./assets/ui/icons/you_arrow.webp"); tex.colorSpace = THREE.SRGBColorSpace;
    this.youSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    this.youSprite.scale.set(0.55, 0.55, 1); this.youSprite.renderOrder = 10;
    world.setYou(this.youSprite);
    this.aimSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true, color: 0xff8a7a }));
    this.aimSprite.scale.set(0.5, 0.5, 1); this.aimSprite.renderOrder = 10; this.aimSprite.visible = false;
    world.stage.scene.add(this.aimSprite);
    const unlock = () => { audio.unlock(); audio.preload(["bell_call", "whistle_fake", "pop_hit", "pop_miss", "popper_fire", "capsule_grab", "capsule_stash", "perfect", "blunder"]); };
    addEventListener("pointerdown", unlock, { once: false }); addEventListener("keydown", unlock, { once: false });
    cg.onSettings((s) => audio.setPlatformMuted(!!s.muteAudio));
    audio.setPlatformMuted(cg.muteAudio());
    cg.onAuthChange(() => store.flush());
  }

  /** the player's cosmetics as wire indices */
  cos(): Cosmetics {
    const e = store.get().equip;
    return { mask: cosIndex(e.mask), colour: cosIndex(e.color), fx: cosIndex(e.reveal), hat: cosIndex(e.hat), ray: cosIndex(e.ray) };
  }

  start() {
    const p = store.get();
    tel.setIdentity(p.pid, cg.hosted() ? "cg" : fp.hosted() ? "fp" : "web");
    tel.ev("boot", { ...tel.device(), tier: this.world.stage.tier, newcomer: !p.flags.played, level: p.level });
    const now = Date.now();
    const away = collectIncome(p, now); ensureDaily(p, now); store.update(() => {});
    if (away > 0) this.hud.toast(`Your Pals made ${away} coins while you were gone`, "coin");
    const newcomer = !p.flags.played;
    // a first visit drops straight into a round (zero clicks); returning players see the door (one click to PLAY)
    // a CrazyGames invite (or instant-multiplayer launch) goes straight into that room / a public match
    // Freeplay: players arrive from the arcade's game page and must meet the menu first (its stage rule), not a round
    const invite = cg.inviteCode() || fp.joining();
    cg.onJoinRoom((c) => this.play(c)); fp.onJoin((c) => this.play(c));
    if (invite) this.play(invite);
    else if (!fp.hosted() && (newcomer || cg.isInstantMultiplayer() || new URLSearchParams(location.search).has("autoplay"))) this.play();
    else this.hud.door(true);
    requestAnimationFrame(this.frame);
  }

  /** join a table: public quick-join, a friend's room code, or a new private room ("new"). The room server is ours
   *  (Dallas); when it can't be reached a public join falls back to an offline table of bots, so the game always plays. */
  async play(code?: string) {
    const p = store.get();
    this.hud.door(false);
    // the name others see: the CrazyGames username when logged in, else the generated name (never typed text)
    const cgName = (await cg.username().catch(() => null)) ?? fp.username();
    const info = { name: cgName ?? (isGenName(p.name) ? p.name : genName()), pid: p.pid, cos: this.cos(), newcomer: !p.flags.played };
    this.feed?.leave(); this.feed = null;
    const target = code === "new" ? { kind: "new" as const } : code ? { kind: "code" as const, code } : { kind: "public" as const };
    const solo = !WS_BASE || new URLSearchParams(location.search).has("solo");
    const feed = await NetFeed.open(WS_BASE, info, target as any, { solo, plat: cg.hosted() ? "cg" : fp.hosted() ? "fp" : "web", build: tel.BUILD });
    if (feed.error && !feed.online && target.kind === "code") { this.hud.flash("NO ROOM WITH THAT CODE", "red"); this.hud.door(true); return; }
    this.feed = feed;
    tel.ev("join", { online: feed.online, target: target.kind });
    this.publishedCode = "";
    audio.ambience(true);
  }
  leave() { this.feed?.leave(); this.feed = null; cg.roomLeft(); this.setPlaying(false); audio.ambience(false); audio.music("bed_menu"); this.hud.door(true); }

  private setPlaying(on: boolean) { if (on === this.playing) return; this.playing = on; on ? cg.gameplayStart() : cg.gameplayStop(); if (on) fp.gameplayStart(); }

  /** store/QA director: a fixed clock instead of rAF (frame-exact video), and a camera it places itself */
  manual = false; private vNow = 0;
  directorCam: { x: number; z: number; dist: number; pitch?: number } | null = null;
  /** first-minute funnel (2026-10-08): each step once per session, with seconds since the first play */
  private fun = new Set<string>(); private playT0 = -1;
  private funnel(pub: any, me: any) {
    if (!pub || !me || pub.phase !== "play") return;
    const now = performance.now() / 1000; if (this.playT0 < 0) this.playT0 = now;
    const mark = (k: string, extra: Record<string, unknown> = {}) => { if (this.fun.has(k)) return; this.fun.add(k); tel.ev("f_" + k, { s: Math.round((now - this.playT0) * 10) / 10, role: me.role, touch: input.input.touch, ...extra }); };
    if (input.input.mx || input.input.mz || input.input.act || input.input.sprint) mark("input");
    if (input.input.mx || input.input.mz) mark("move");
    if (me.role === "sneak" && me.can === "grab") mark("at_stall");
    if (me.role === "sneak" && input.input.act) mark("act");
    if (me.carry) mark("carry");
    if (me.role === "sneak" && me.can === "stash") mark("at_drain");
  }
  step(dt: number) {
    if (!this.vNow) this.vNow = performance.now();   // continue from the real clock the feed has been running on
    this.vNow += dt * 1000; this.tick(this.vNow, dt, Date.now());
  }
  private frame = (t: number) => {
    if (this.manual) return;
    requestAnimationFrame(this.frame);
    const dt = Math.min(0.1, (t - this.last) / 1000); this.last = t;
    this.tick(t, dt, Date.now());
  };
  private tick(t: number, dt: number, wallMs: number) {
    input.poll();
    const f = this.feed;
    this.hud.root.style.display = f ? "" : "none";
    if (!f) {
      if (!this.attract) this.attract = new LocalFeed(null, 4242);
      this.attract.update(dt, wallMs); this.attract.drain();
      const pub = this.attract.pub();
      this.world.sync(pub, dt, t); this.world.placeYou(0, false);
      this.orbit += dt * 0.05;
      const st = this.world.stage, R = 7;
      st.focus.set(Math.sin(this.orbit) * R, 0.6, Math.cos(this.orbit) * R * 0.6 + 2); st.dist = 17;
    } else if (this.attract) { this.attract = null; }
    // the net feed's clock is performance time (its stale-socket check compares against performance.now()); only the
    // offline backdrop above reads the wall clock (the hourly PARADE window)
    // the room code arrives with the seat message (after the join resolves): publish it to CrazyGames (invite button,
    // instant multiplayer, "join friend") as soon as it exists
    if (f && f.online && f.roomCode() && f.roomCode() !== this.publishedCode) { this.publishedCode = f.roomCode(); cg.roomJoined(this.publishedCode); fp.roomJoined(this.publishedCode); }
    if (f && f.update(dt, t)) {
      this.pub = f.pub(); this.priv = f.priv();
      this.sendInput();
      for (const e of f.drain()) { this.onEvent(e); if (QA_BOT) this.qaEvents.push(e); }
      this.onView(this.pub, this.priv);
      this.world.sync(this.pub, dt, t);
      this.follow();
      this.hud.update(this.pub, this.priv, dt);
      this.funnel(this.pub, this.priv);
    }
    if (this.directorCam) { const c = this.directorCam; this.world.stage.focus.set(c.x, 0.6, c.z); this.world.stage.dist = c.dist; if (c.pitch) this.world.stage.pitch = c.pitch; }
    this.world.render(dt);
    // after the render: the camera has moved this frame, so the guide projects onto what is on screen
    if (f && this.pub && this.priv) this.hud.guide(this.guideTarget(this.priv, this.pub));
    input.endFrame();
  }

  private qaBrain: BotBrain | null = null; private qaKey = "";
  private qaEvents: SimEvent[] = [];
  private sendInput() {
    const f = this.feed!, me = this.priv!, i = input.input;
    if (QA_BOT) {
      const key = `${this.pub!.round}:${me.role}`;
      if (key !== this.qaKey) { this.qaKey = key; this.qaBrain = me.role === "sneak" ? new BotSneak(7, HUMAN_SNEAK) : new BotInspector(7); }
      const out = this.qaBrain!.update(this.pub!, me, this.qaEvents); this.qaEvents = [];
      f.input(out.input); for (const p of out.pops) { f.pop(p.id, p.aim); this.popFx(p.id); } if (out.whistle) f.whistle();
      return;
    }
    const playing = this.pub!.phase === "play" && !this.hud.modalOpen();
    f.input({ mx: playing ? i.mx : 0, mz: playing ? i.mz : 0, sprint: playing && i.sprint, act: playing && i.act });
    if (!playing || me.role !== "inspector") return;
    if (i.whistle) f.whistle();
    if (i.popAt) {
      const hit = this.world.pick(i.popAt.x, i.popAt.y, me.me);
      if (hit) {
        if (input.input.touch) { f.mark(hit.id, hit.aim); this.mark = hit.id; }
        else { f.pop(hit.id, hit.aim); this.popFx(hit.id); }
      }
    }
    if (i.popFire && this.mark) { f.pop(this.mark, null); this.popFx(this.mark); }
  }
  private popFx(target: number) {
    const a = this.world.bodyPos(this.priv!.me), b = this.world.bodyPos(target);
    if (a && b) this.world.fx.ray(this.rayKind(), a.x, 1.0, a.z, b.x, 0.9, b.z);
    audio.play("popper_fire");
  }
  /** everyone else sees the Inspector's shot too (it was drawn only on the shooter's screen): the ray from his body to
   *  the target, in his equipped ray cosmetic */
  private otherRay(by: number, x: number, z: number) {
    if (!this.priv || by === this.priv.me) return;   // the shooter drew it already (popFx)
    const a = this.world.bodyPos(by); if (!a) return;
    const id = cosIds("ray")[this.pub?.inspectorCos?.ray ?? 0] ?? "ray_confetti";
    this.world.fx.ray(this.rayKindOf(id), a.x, 1.0, a.z, x, 0.9, z);
    audio.play("popper_fire", { dist: Math.hypot(a.x - (this.world.stage.focus.x), a.z - this.world.stage.focus.z) });
  }
  private rayKind() { return this.rayKindOf(store.get().equip.ray); }
  private rayKindOf(rayId: string) { const r = rayId.replace("ray_", ""); return ({ confetti: "fx_confetti", bubble: "fx_bubbles", spark: "fx_stars", rainbow: "fx_confetti", snow: "fx_stars", gold: "fx_coins" } as Record<string, string>)[r] ?? "fx_confetti"; }

  /** first times guided, then off: the guide arrow shows until the player has stashed a few capsules */
  guided(): boolean { return !!this.pub?.onboarding || store.get().stats.stashes < GUIDE_UNTIL_STASHES; }

  /** the guide arrow: nearest stocked stall (or nearest drain while carrying), projected to the screen */
  private guideTarget(me: PrivateView, pub: PublicView): { x: number; y: number; rot: number; label: string } | null {
    if (me.role !== "sneak" || pub.phase !== "play" || me.respawnIn > 0 || !this.guided()) return null;
    if (me.can === "grab" || me.can === "stash") return null;   // there: the verb banner says HOLD
    const p = this.world.bodyPos(me.me); if (!p) return null;
    let best: { x: number; z: number; y: number } | null = null, bd = Infinity;
    const consider = (x: number, z: number, y: number) => { const d = Math.hypot(x - p.x, z - p.z); if (d < bd) { bd = d; best = { x, z, y }; } };
    if (me.carry) for (const d of DRAINS) consider(d.x, d.z, 0.2);
    else STALLS.forEach((s, i) => { if (pub.stalls[i]) consider(s.capsule.x, s.capsule.z, this.world.town.signY(i) + 0.45); });
    if (!best) return null;
    const b = best as { x: number; z: number; y: number };
    const label = me.carry ? "DRAIN" : "CAPSULE";
    const el = this.world.stage.renderer.domElement, w = el.clientWidth, h = el.clientHeight;
    const v = new THREE.Vector3(b.x, b.y, b.z).project(this.world.stage.camera);
    let sx = (v.x * 0.5 + 0.5) * w, sy = (-v.y * 0.5 + 0.5) * h;
    if (v.z > 1) { sx = w - sx; sy = h - sy; }                      // behind the camera: mirror
    const m = 52, bob = Math.sin(performance.now() / 160) * 6;
    if (v.z <= 1 && sx > m && sx < w - m && sy > m + 40 && sy < h - m) return { x: sx, y: sy - 46 + bob, rot: 0, label };
    // off screen: pin to the edge on the line from the centre, pointing at it
    const cx = w / 2, cy = h / 2, dx = sx - cx, dy = sy - cy;
    const k = Math.min((w / 2 - m) / Math.max(1e-3, Math.abs(dx)), (h / 2 - m - 20) / Math.max(1e-3, Math.abs(dy)));
    return { x: cx + dx * k, y: cy + dy * k, rot: (Math.atan2(dy, dx) * 180) / Math.PI - 90, label };
  }

  private follow() {
    const me = this.priv!, pub = this.pub!;
    const p = this.world.bodyPos(me.me) ?? (pub.inspector ? this.world.bodyPos(pub.inspector) : null);
    if (p) this.world.stage.setFollow(p.x, p.z, me.role === "inspector");
    if (me.me !== this.world.meId) this.youUntil = performance.now() + (store.get().flags.played ? 2500 : 8000);
    this.world.meId = me.me;
    const bob = Math.sin(performance.now() / 180) * 0.08;
    this.world.placeYou(me.me, pub.phase !== "results" && (performance.now() < this.youUntil || pub.phase === "intro"));
    if (this.youSprite) this.youSprite.position.y += bob;
    // the Inspector's aim marker: the townie under the cursor (desktop) or the marked one (touch)
    let aimId = 0;
    if (me.role === "inspector" && pub.phase === "play") aimId = input.input.touch ? this.mark : (input.hover.x >= 0 ? this.world.pick(input.hover.x, input.hover.y, me.me)?.id ?? 0 : 0);
    const ap = aimId ? this.world.headPos(aimId) : null;
    if (this.aimSprite) { this.aimSprite.visible = !!ap; if (ap) this.aimSprite.position.set(ap.x, ap.y + 0.5 + bob, ap.z); }
    document.body.style.cursor = aimId ? "crosshair" : "";
  }

  /** phase edges: the gameplay clock, music, ads */
  private onView(pub: PublicView, me: PrivateView) {
    // the round's intro card + the Crier's opening line, exactly once per round, even if we joined mid-intro
    if (pub.phase === "intro" && this.introRound !== pub.round && me.me) {
      this.introRound = pub.round;
      this.hud.intro({ k: "round_start", n: pub.round, parade: pub.parade, onboarding: pub.onboarding, inspector: pub.inspector, inspectorSeat: pub.inspectorSeat }, me);
      audio.say(pub.parade ? "crier_parade" : "crier_intro");
    }
    if (pub.phase !== this.phase) {
      const was = this.phase; this.phase = pub.phase;
      if (pub.phase === "play") { tel.ev("play", { round: pub.round, onboarding: pub.onboarding, role: this.priv?.role }); this.setPlaying(true); audio.music(pub.parade ? "bed_parade" : "bed_town"); }
      if (pub.phase === "intro") { this.setPlaying(false); audio.music("bed_menu", 0.6); }
      if (pub.phase === "results") { this.setPlaying(false); if (was === "play") this.maybeAd(); }
    }
    if (pub.phase === "play" && !pub.parade && pub.phaseLeft <= 30) audio.music("bed_tension", 2.5);
  }

  private async maybeAd() {
    this.roundsSinceAd++;
    if (this.roundsSinceAd < T.MIDGAME_EVERY_ROUNDS || Date.now() - this.sessionStart < T.MIDGAME_NOT_BEFORE_SEC * 1000) return;
    this.roundsSinceAd = 0;
    input.setEnabled(false);
    await cg.midgameAd(() => audio.setPlatformMuted(true));
    audio.setPlatformMuted(cg.muteAudio());
    input.setEnabled(true);
  }

  private onEvent(e: SimEvent) {
    const w = this.world, me = this.priv!;
    const at = (id: number) => w.bodyPos(id);
    const dist = (x: number, z: number) => Math.hypot(x - w.stage.focus.x, z - w.stage.focus.z);
    switch (e.k) {
      case "round_start": this.mark = 0; break;     // the intro card is driven by the phase (see onView): it never misses
      case "call": audio.play("bell_call"); audio.say(`crier_call_${e.call}`); this.hud.call(e.call, true); break;
      case "whistle": audio.play("whistle_fake"); this.hud.call(e.call, false); if (me.role === "sneak" && firstTime(store.get(), "first_whistle", Date.now())) this.hud.coach("whistle"); break;
      case "answer": if (e.perfect) { audio.play("perfect"); this.hud.flash("PERFECT!", "gold"); } break;
      case "blunder": { const p = w.headPos(e.id); if (p) { w.fx.question(p.x, p.y + 0.35, p.z); audio.play("blunder", { dist: dist(p.x, p.z) }); } break; }
      case "blundered": this.hud.flash(e.why === "whistle" ? "THAT WAS A WHISTLE!" : e.why === "moved" ? "YOU MOVED!" : "TOO SLOW!", "red"); break;
      case "pop_hit": {
        this.otherRay(e.by, e.x, e.z);
        const p = new THREE.Vector3(e.x, 1.0, e.z);
        const kind = ["fx_confetti", "fx_feathers", "fx_bubbles", "fx_leaves", "fx_stars", "fx_coins"][e.cos.fx] ?? "fx_confetti";
        w.fx.burst(kind, p.x, p.y, p.z); audio.play("pop_hit", { dist: dist(e.x, e.z) }); audio.say("crier_catch"); w.stage.kick(0.12);
        if (e.by === me.me) { this.hud.flash("CAUGHT A SNEAK! POP REFUNDED", "gold"); cg.happytime(); }
        break;
      }
      case "pop_miss": this.otherRay(e.by, e.x, e.z); audio.play("pop_miss", { dist: dist(e.x, e.z) }); audio.play("panic_scatter", { dist: dist(e.x, e.z) }); audio.say("crier_miss"); w.fx.puff(e.x, e.z, 5); if (e.by === me.me) this.hud.flash(e.id >= 0 ? "INNOCENT! −1 POP" : "MISSED · NO POP USED", "red"); break;
      case "pop_reject": this.hud.flash({ cooldown: "RELOADING…", charges: "OUT OF POPS · WAIT FOR THE NEXT", range: "TOO FAR", sight: "CAN'T SEE THEM", phase: "" }[e.why], "red"); break;
      case "grabbed": tel.ev("grab"); audio.play("capsule_grab"); this.hud.flash(e.golden ? "GOLDEN CAPSULE!" : "GOT ONE! STASH IT IN A DRAIN", "gold"); break;
      case "stash": tel.ev("stash", { r: e.rarity }); audio.play("capsule_stash"); this.hud.stashed(e.rarity, e.golden); break;
      case "vault": this.hud.vault(e.n, e.goal); audio.play("vault_tick"); break;
      case "glint": { const p = at(e.id); if (p) w.fx.glint(p.x, 1.2, p.z); audio.play("glint", { dist: dist(e.x, e.z) }); break; }
      case "caught": this.hud.flash("YOU WERE CAUGHT! BACK ON THE BUS…", "red"); break;
      case "respawn": this.hud.respawned(e.costume); break;
      case "bus": if (e.state === "arriving") audio.play("bus_arrive", { dist: dist(10, 20) }); break;
      case "regen": this.hud.flash("+1 POP RECHARGED", "teal"); break;
      case "round_end": audio.play(e.winner === "sneak" ? "sneaks_win" : "inspector_wins"); audio.say(e.winner === "sneak" ? "crier_sneaks_win" : "crier_inspector_win"); break;
      case "results": this.payout(e); break;
    }
  }

  private payout(e: Extract<SimEvent, { k: "results" }>) {
    tel.ev("round", { role: e.role, win: e.win, stashes: e.stashes.length, perfects: e.perfects, answered: e.answered, blunders: e.blunders, catches: e.catches, misses: e.misses, popped: e.popped });
    const r: RoundResult = { role: e.role, won: e.win, stashed: e.stashes, perfects: e.perfects, catches: e.catches, innocentPops: e.misses, popped: e.popped, ignoredWhistles: e.whistlesIgnored, parade: e.parade };
    let pay!: ReturnType<typeof applyRound>;
    store.update((p) => { pay = applyRound(p, r, Date.now()); p.flags.played = p.flags.played || Date.now(); });
    this.hud.results(e, pay);
    if (pay.levelUps.length) audio.play("level_up");
    if (e.win) cg.happytime();
  }
}
